import { ArrowLeftRight, Merge } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Alert, Button, Modal } from "@/components/common";
import { useMergeClients } from "@/hooks/useClients";
import type {
  ClientListItem,
  ClientMergeChoices,
  DuplicatePair,
  MergeField,
} from "@/types/clients";
import { formatDateTime, RodoVerifiedMark } from "./RodoStatusBadges";

interface MergeClientsModalProps {
  pair: DuplicatePair | null;
  onClose: () => void;
}

const FIELDS: {
  key: MergeField;
  label: string;
  value: (c: ClientListItem) => string | null | undefined;
}[] = [
  { key: "firstName", label: "Imię", value: (c) => c.firstName },
  { key: "lastName", label: "Nazwisko", value: (c) => c.lastName },
  { key: "pesel", label: "PESEL", value: (c) => c.peselMasked },
  { key: "phone", label: "Telefon", value: (c) => c.phoneMasked },
  { key: "email", label: "E-mail", value: (c) => c.emailMasked },
  { key: "nip", label: "NIP", value: (c) => c.nip },
];

/**
 * Merges two records of one client. The agent picks the record that stays and, field by field,
 * which value is kept. Consent requests and scans of the merged-in record stay unchanged as
 * evidence and are shown under the surviving record.
 */
export function MergeClientsModal({ pair, onClose }: MergeClientsModalProps) {
  const merge = useMergeClients();
  // State resets per pair: the parent keys this modal by the pair.
  // false = the older record (pair.first) stays.
  const [swapped, setSwapped] = useState(false);
  const [take, setTake] = useState<ClientMergeChoices>({});

  if (!pair) {
    return null;
  }

  const target = swapped ? pair.second : pair.first;
  const source = swapped ? pair.first : pair.second;

  function handleMerge() {
    merge.mutate(
      {
        targetClientId: target.client.id,
        sourceClientId: source.client.id,
        take,
      },
      {
        onSuccess: (result) => {
          toast.success(
            `Połączono klientów: ${result.client.firstName} ${result.client.lastName}` +
              (result.movedConsentRequests > 0
                ? ` · wnioski RODO z duplikatu: ${result.movedConsentRequests}`
                : ""),
          );
          onClose();
        },
      },
    );
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Połącz dwa rekordy klienta"
      className="max-w-3xl"
    >
      <div className="space-y-4">
        {merge.isError && (
          <Alert variant="error">
            {(merge.error as { message?: string }).message ??
              "Nie udało się połączyć klientów"}
          </Alert>
        )}

        <p className="text-sm text-sub">
          Zaznacz, która wartość ma zostać. Rekord po lewej zostaje, prawy
          zostanie do niego dołączony i zniknie z listy.
        </p>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-edge text-left text-xs text-dim">
                <th className="py-2 pr-3 font-medium">Pole</th>
                <th className="py-2 pr-3 font-medium">
                  Zostaje · dodany {formatDateTime(target.client.createdAt)}
                </th>
                <th className="py-2 font-medium">
                  Dołączany · dodany {formatDateTime(source.client.createdAt)}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-edge-soft">
              {FIELDS.map((f) => {
                const keep = f.value(target.client) ?? null;
                const other = f.value(source.client) ?? null;
                const same = keep === other;
                const fromSource = take[f.key] === true;
                return (
                  <tr key={f.key}>
                    <td className="py-2 pr-3 text-dim">{f.label}</td>
                    {[false, true].map((isSource) => {
                      const value = isSource ? other : keep;
                      const checked = isSource ? fromSource : !fromSource;
                      return (
                        <td key={String(isSource)} className="py-2 pr-3">
                          {same ? (
                            !isSource && (
                              <span className="text-main">{value ?? "—"}</span>
                            )
                          ) : (
                            <label className="flex cursor-pointer items-center gap-2">
                              <input
                                type="radio"
                                name={`merge-${f.key}`}
                                checked={checked}
                                onChange={() => {
                                  setTake((prev) => ({
                                    ...prev,
                                    [f.key]: isSource,
                                  }));
                                }}
                                className="accent-adara-600"
                              />
                              <span
                                className={
                                  checked ? "font-medium text-main" : "text-sub"
                                }
                              >
                                {value ?? "—"}
                              </span>
                            </label>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              <tr>
                <td className="py-2 pr-3 text-dim">RODO</td>
                <td className="py-2 pr-3">
                  <RodoVerifiedMark rodo={target.client.rodo} />
                  <p className="text-xs text-faint">
                    wnioski: {target.consentRequestCount}
                  </p>
                </td>
                <td className="py-2">
                  <RodoVerifiedMark rodo={source.client.rodo} />
                  <p className="text-xs text-faint">
                    wnioski: {source.consentRequestCount}
                  </p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <Alert variant="info">
          Zgody RODO: zostaje ta potwierdzona później. Wnioski i skany
          dołączanego rekordu zostają bez zmian jako dowód i będą widoczne przy
          kliencie, który zostaje; jego oczekujące wnioski zostaną anulowane.
          Połączenie zapisujemy w historii.
        </Alert>

        <div className="flex flex-col-reverse gap-3 border-t border-edge-soft pt-4 sm:flex-row sm:justify-between">
          <Button
            variant="ghost"
            type="button"
            leftIcon={<ArrowLeftRight size={16} aria-hidden />}
            onClick={() => {
              setSwapped((s) => !s);
              setTake({});
            }}
          >
            Zamień, który zostaje
          </Button>
          <div className="flex gap-3">
            <Button variant="ghost" type="button" onClick={onClose}>
              Anuluj
            </Button>
            <Button
              type="button"
              isLoading={merge.isPending}
              leftIcon={<Merge size={16} aria-hidden />}
              onClick={handleMerge}
            >
              Połącz
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
